import { Fragment, useEffect, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import * as api from "../../api";
import { useDispatch, useSelector } from "react-redux";
import { Container } from "react-bootstrap";
import RepairJourney from "../RepairJourney";
import { detectMobile } from "../../utils/formatting";
import { WorksOrderRequest } from "../../models/WorksOrders/WorksOrderRequest";
import { useTranslation } from "react-i18next";
import { getPreviousPageName, saveDMCQuestions } from "../../ducks/redux/actions/FinancialReferral";
import { customersData, showButtonRequestAnotherRepair } from "../../ducks/redux/actions/userChoices";
import { workOrderRepairObject } from "../../ducks/redux/actions/getAddress";
import "bootstrap-icons/font/bootstrap-icons.css";
import "bootstrap/dist/css/bootstrap.min.css";
import '../../utils/commonCss/icons.css';
import "./index.css";
import Button from "../../Button/Button";

interface VulnerabilityAssessmentProps extends RouteComponentProps<any> { }

interface OptionProps {
  option: any;
  questionId: string;
  isMobile: boolean;
  checked: boolean;
  onChange: (questionId: string, e: any) => void;
}

const OptionRenderer: React.FC<OptionProps> = ({ option, questionId, isMobile, checked, onChange }) => (
  <div key={option.value} className={`mb-1 ${isMobile ? 'flex w-full' : 'flex'}`}>
    <fieldset>
      <legend className="sr-only">{questionId}</legend>
      <input
        type="checkbox"
        id={`${questionId}-${option.id}`}
        name={questionId}
        className="checkPos"
        value={option.id}
        aria-label={option.value}
        checked={checked}
        onChange={(e) => onChange(questionId, e)}
      />
    </fieldset>
    <label
      htmlFor={`${questionId}-${option.id}`}
      className={`ml-4 ${isMobile ? 'inline-block mt-1 w-90' : ''}`}
    >
      {option.value}
    </label>
  </div>
)

interface QuestionProps {
  question: any;
  isMobile: boolean;
  userAnswers: { [key: string]: any };
  handleCheckBox: (questionId: string, e: any) => void;
  showAlert: boolean;
  alertMessage: string;
  isFirst?: boolean;
}

const QuestionRenderer: React.FC<QuestionProps> = ({ question, isMobile, userAnswers, handleCheckBox, showAlert, alertMessage, isFirst }) => {
  let gridColsClass = 'grid-cols-1';
  if ((isFirst || question.options.length > 4) && !isMobile) {
    gridColsClass = 'grid-cols-2';
  }
  return (
    <div key={question.id} className={isMobile ? "mobile-padding" : ""}>
      <h2 className="text-purple-100">{question.label}</h2>
      {showAlert && (
        <div className="error mb-4" role="alert" placeholder="alert-message">
          <div className="w-less px-2 py-2 bg-yellow flex items-center">
            <div className="px-4 pr-8">
              <i className="fas fa-exclamation-triangle text-brick-70 w-6 h-6"></i>
            </div>
            <div className="flex items-center">
              <p className="w-full text-left my-2 font-AvantGardeGothic-Md">
                {alertMessage}
              </p>
            </div>
          </div>
        </div>
      )}
      <div className="mb-4">
        <div className={`grid ${gridColsClass}`}>
          {question.options.map((option: any) => (
            <OptionRenderer
              key={option.value}
              option={option}
              questionId={question.questionId}
              isMobile={isMobile}
              checked={userAnswers[question.questionId]?.includes(option.id) || false}
              onChange={handleCheckBox}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

const VulnerabilityAssessment: React.FC<VulnerabilityAssessmentProps> = (props: any) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [vulnerabilityQuestions, setvulnerabilityQuestions] = useState<any>([]);
  const [userAnswers, setUserAnswers] = useState<{ [key: string]: any }>({});
  const [error, setError] = useState("");
  const [assessmentScoreFailed, setAssessmentScoreFailed] = useState(false);
  const [workOrderFailed, setWorkOrderFailed] = useState(false);
  const isMobile = detectMobile();
  const dmcQuestionsFromReducer = useSelector((state: any) => state?.financialReferralReducer?.DMCData);
  const workOrderObject = useSelector((state: any) => state?.addresses?.workOrderRepair);

  useEffect(() => {
    dispatch(getPreviousPageName("customer"));
    AddVulnerabilityQuestions();
    getCustomerInfoForHeaderJourney(workOrderObject?.customerIdentifier);
  }, []);

  const getCustomerInfoForHeaderJourney = async (custId: string) => {
    try {
      const res = await api.getCustomer(custId);
      dispatch(customersData(res));
    } catch (e: any) {
      console.error("customer api failed", e);
      clearAllReduxUsed();
      navigateTo("/GenericError");
    }
  }

  const AddVulnerabilityQuestions = () => {
    setvulnerabilityQuestions((prev: any[]) => [...prev, ...[2, 6, 5]
      .map(i => dmcQuestionsFromReducer?.additionalInfo[i]).filter(Boolean).map(obj => ({ ...obj }))]);
  }

  const handleCheckBox = (questionId: string, e: any) => {
    const selectedAnswers = userAnswers[questionId] || [];
    if (e.target.checked) {
      selectedAnswers.push(e.target.value);
    } else {
      const index = selectedAnswers.indexOf(e.target.value);
      if (index > -1) {
        selectedAnswers.splice(index, 1);
      }
    }
    setUserAnswers((prevAnswers) => ({
      ...prevAnswers,
      [questionId]: selectedAnswers,
    }));
  }

  const bindVulnerabilityQuestions = () => {
    return vulnerabilityQuestions?.map((question: any, idx: number) => {
      const unanswered: boolean = !!error && (!userAnswers[question.questionId] || userAnswers[question.questionId].length === 0);
      const isFirst = idx === 0;
      return (
        <div key={question.id}>
          {(isFirst && assessmentScoreFailed) && (
            <p className="solid-red-text">{t('Assessment_Score_Error1')}<br /><br />{t('Assessment_Score_Error2')}</p>
          )}
          {(isFirst && workOrderFailed) && (
            <p className="solid-red-text">{t('WorkOrder_Error1')}<br /><br />{t('WorkOrder_Error2')}</p>
          )}
          <QuestionRenderer
            question={question}
            isMobile={isMobile}
            userAnswers={userAnswers}
            handleCheckBox={handleCheckBox}
            showAlert={unanswered}
            alertMessage={error}
            isFirst={isFirst}
          />
        </div>
      );
    });
  }

  const saveAssessmentInformation = () => {
    const assessmentInformation = {
      type: "DampAndMould",
      outcome: workOrderObject?.assessmentInformation?.outcome ?? null,
      communicationPreference: workOrderObject?.assessmentInformation?.communicationPreference ?? null,
      identifier: workOrderObject?.assessmentInformation?.identifier ?? null,
      supplementaryQuestionResponses: Object.entries(userAnswers).map(([questionId, answer]) => {
        const question = vulnerabilityQuestions.find((q: any) => q.questionId === questionId);
        return {
          questionId: questionId,
          questionLabel: question?.label || "",
          responseId: Array.isArray(answer) ? answer : [answer],
        };
      })
    }
    workOrderObject.assessmentInformation = assessmentInformation;
  }

  const UpdateWorkOrder = async () => {
    workOrderObject.priority = workOrderObject.priorityCode; // set priority code value to work order object
    const _worksOrderRequest = new WorksOrderRequest(workOrderObject);
    const woRequestParameters =
      _worksOrderRequest.updateWorkOrderParameter(workOrderObject);
    await api.updateWorkOrderRepair(
      woRequestParameters,
      workOrderObject.id,
      "eTag"
    ).then((res: any) => {
      if (res === 204) {
        workOrderObject.isPendingAssessment = false
        dispatch(workOrderRepairObject(workOrderObject));
        dispatch(showButtonRequestAnotherRepair(true));
        clearAllReduxUsed();
        navigateTo(`/RepairFinalDetails`);
      }
    }).catch(e => {
      console.error(e);
      setWorkOrderFailed(true);
    });
  };

  const navigateTo = (path: string) => {
    props.history.push(path);
  };

  const handleContinue = async () => {
    setAssessmentScoreFailed(false);
    setWorkOrderFailed(false);
    const unansweredQuestions = vulnerabilityQuestions
      ?.some((question: any) => {
        const answer = userAnswers[question.questionId];
        return !answer || answer.length === 0;
      });
    if (unansweredQuestions) {
      setError("Select an option to continue");
    } else {
      saveAssessmentInformation();
      let vulnerabilityAssessmentPriorityValue = null;
      try {
        vulnerabilityAssessmentPriorityValue = await api.assessmentScoreDetermination(workOrderObject?.assessmentInformation);
      } catch (e) {
        console.error(e);
        setAssessmentScoreFailed(true);
        return;
      }
      const assessmentOutcomes = {
        adjustedRiskRating: vulnerabilityAssessmentPriorityValue?.adjustedRiskRating,
        totalScore: vulnerabilityAssessmentPriorityValue?.totalScore,
        riskRating: vulnerabilityAssessmentPriorityValue?.riskRating,
        vulnerabilityScore: vulnerabilityAssessmentPriorityValue?.vulnerabilityScore,
        vulnerabilityRiskRating: vulnerabilityAssessmentPriorityValue?.vulnerabilityRiskRating,
        communicationPreference: workOrderObject?.assessmentInformation?.communicationPreference ?? null,
        contractorPropertyRating: workOrderObject?.assessmentInformation?.outcome?.contractorPropertyRating ?? null
      };
      workOrderObject.assessmentInformation.outcome = assessmentOutcomes;
      UpdateWorkOrder();
    }
  };

  const clearAllReduxUsed = () => {
    dispatch(saveDMCQuestions(null));
    dispatch(getPreviousPageName(''));
    dispatch(customersData(null));
  }

  return (
    <Container id="supp-main-container" className={isMobile ? "ctn-fht1 px-4 mobile-padding" : "ctn-fht1 px-4"} role="main">
      <Fragment>
        <div id="backmodal-div1" className="mx-auto mb-0" >
          <div className="back-and-journey">
            <div className=""></div>
            {<RepairJourney />}
          </div>
        </div>
        <div className={isMobile ? "mobile-padding" : ""}>
          <div className="relative pb-2 bg-white">
            {bindVulnerabilityQuestions()}
          </div>
          <div className={isMobile ? "flex gap-4 items-center pb-4 pt-2 mobile-btn-row" : "flex gap-4 items-center pb-4 pt-2"}>
            <a
              id="exitmodal-btn1"
              className="back-btn text-teal exit-btn a-style cursor-pointer"
              href={`/WorkOrder360View/${workOrderObject?.id}`}
              onClick={clearAllReduxUsed}
            >
              {t("exit_button")}
            </a>
            <Button
              id="btn_custmerDetails"
              onClick={handleContinue}
              className="button-class"
            >
              {t("Submit_Assessment")}
            </Button>
          </div>
        </div>
      </Fragment>
    </Container>
  );
};
export default VulnerabilityAssessment;